<template>
  <div class="relative isolate px-6 py-24 sm:py-32 lg:px-8">
    <div class="absolute inset-0 -z-10 bg-cover bg-center bg-gray-900 bg-no-repeat" 
        >
    </div>
    <div class="absolute inset-0 -z-10 bg-gray-900/75"></div>
    <div class="mx-auto max-w-4xl text-center" id="price">
      <h2 class="text-base/7 font-semibold text-indigo-400">Pricing</h2>
      <p class="mt-2 text-balance text-5xl font-semibold tracking-tight text-white sm:text-6xl">Choose the right plan for you</p>
    </div>
    <p class="mx-auto mt-6 max-w-2xl text-pretty text-center text-lg font-medium text-gray-400 sm:text-xl/8">Choose an affordable plan that’s packed with the best features for engaging your audience, creating customer loyalty, and driving sales.</p>
    
    <div class="mt-8 flex justify-center">
      <!-- Added horizontal scrolling for mobile -->
      <div class="inline-flex rounded-full bg-gray-800 p-1 overflow-x-auto whitespace-nowrap sm:flex-wrap">
        <button 
          @click="activeTierSet = 1" 
          class="rounded-full px-4 py-2 text-sm font-semibold transition-colors mr-2 sm:mr-0"
          :class="{ 'bg-indigo-500 text-white': activeTierSet === 1, 'text-gray-300 hover:bg-gray-600': activeTierSet !== 1 }">
          Portfolio Class
        </button>
        <button 
          @click="activeTierSet = 2" 
          class="rounded-full px-4 py-2 text-sm font-semibold transition-colors mr-2 sm:mr-0"
          :class="{ 'bg-indigo-500 text-white': activeTierSet === 2, 'text-gray-300 hover:bg-gray-600': activeTierSet !== 2 }">
          Business Class
        </button>
        <button 
          @click="activeTierSet = 3" 
          class="rounded-full px-4 py-2 text-sm font-semibold transition-colors mr-2 sm:mr-0"
          :class="{ 'bg-indigo-500 text-white': activeTierSet === 3, 'text-gray-300 hover:bg-gray-600': activeTierSet !== 3 }">
          Enterprise Class
        </button>
        <button 
          @click="activeTierSet = 4" 
          class="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
          :class="{ 'bg-indigo-500 text-white': activeTierSet === 4, 'text-gray-300 hover:bg-gray-600': activeTierSet !== 4 }">
          MultiLevel Marketing System / Social Media System / Custom Systems / E-Commerce
        </button>
      </div>
    </div>
    
    <div class="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-2 lg:gap-x-4">
      <div 
        v-for="tier in currentTiers" 
        :key="tier.id" 
        :class="[
          tier.featured ? 'z-20 ring-indigo-500 bg-gray-800' : 'ring-white/10 bg-white/[0.025]',
          'rounded-3xl p-8 ring-1 sm:p-10',
          'transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg',
        ]">
        <template v-if="tier.name === 'Custom System I'">
          <h3 :id="tier.id" :class="[tier.featured ? 'text-indigo-400' : 'text-indigo-400', 'text-base/7 font-semibold']">{{ tier.name }}</h3>
          <p class="mt-4 flex flex-col items-center">
            <span :class="[tier.featured ? 'text-white' : 'text-white', 'text-5xl font-semibold tracking-tight']">{{ formattedCustomPrice }}</span>
            <span :class="[tier.featured ? 'text-gray-400' : 'text-gray-400', 'text-base']">{{ tier.priceSuffix }}</span>
          </p>
          <p :class="[tier.featured ? 'text-gray-300' : 'text-gray-300', 'mt-6 text-base/7']">{{ tier.description }}</p>
          <ul role="list" :class="[tier.featured ? 'text-gray-300' : 'text-gray-300', 'mt-8 space-y-3 text-sm/6 sm:mt-10']">
            <li v-for="feature in tier.features" :key="feature.name" 
                class="flex gap-x-3 cursor-pointer" 
                @click="toggleFeature(feature)">
              <CheckIcon 
                :class="[
                  tier.featured ? 'text-indigo-400' : 'text-indigo-400', 
                  'h-6 w-5 flex-none',
                  { 'line-through text-red-500': !feature.isSelected }
                ]" aria-hidden="true" />
              <span :class="{ 'line-through text-red-500': !feature.isSelected }">{{ feature.name }}</span>
            </li>
          </ul>
        </template>
        <template v-else>
          <h3 :id="tier.id" :class="[tier.featured ? 'text-indigo-400' : 'text-indigo-400', 'text-base/7 font-semibold']">{{ tier.name }}</h3>
          <p class="mt-4 flex flex-col items-center">
            <span :class="[tier.featured ? 'text-white' : 'text-white', 'text-5xl font-semibold tracking-tight']">{{ tier.priceMonthly }}</span>
            <span :class="[tier.featured ? 'text-gray-400' : 'text-gray-400', 'text-base']">{{ tier.priceSuffix }}</span>
          </p>
          <p :class="[tier.featured ? 'text-gray-300' : 'text-gray-300', 'mt-6 text-base/7']">{{ tier.description }}</p>
          <ul role="list" :class="[tier.featured ? 'text-gray-300' : 'text-gray-300', 'mt-8 space-y-3 text-sm/6 sm:mt-10']">
            <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
              <CheckIcon :class="[tier.featured ? 'text-indigo-400' : 'text-indigo-400', 'h-6 w-5 flex-none']" aria-hidden="true" />
              {{ feature }}
            </li>
          </ul>
        </template>
        <a :href="tier.href" :aria-describedby="tier.id" 
            :class="[
             tier.featured ? 'bg-indigo-500 text-white hover:bg-indigo-400 focus-visible:outline-indigo-500' : 'bg-white/10 text-white ring-1 ring-inset ring-white/5 hover:bg-white/20 focus-visible:outline-white/75', 
             'mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10'
            ]">
          Get started today
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { CheckIcon } from '@heroicons/vue/20/solid'

const FEATURE_COST = 30000;
const BASE_PRICE_CUSTOM_SYSTEM_I = 80000;

const tiers1 = [
  {
    name: 'Portfolio Class',
    id: 'tier-hobby-1',
    href: '#',
    priceMonthly: '₱5,000',
    priceSuffix: 'One Time Payment',
    description: "The perfect plan for business startups seeking to build their portfolios.",
    features: ['Landing Page', 'Contact Form', 'Search Engine Optimization', '3 Months Hosting', '3 Months Domain', '+ ₱1000 for 1 Year Domain and Hosting (Optional)'],
    featured: false,
  },
  {
    name: 'Portfolio Class +',
    id: 'tier-enterprise-1',
    href: '#',
    priceMonthly: '₱7,500',
    priceSuffix: '₱1,500/month',
    description: 'Dedicated Support to your website, continuous improvement and support with additional features to booth.',
    features: ['3 Pages', '5 Email Accounts for your Domain', 'Contact Form' , 'Search Engine Optimization', '3 Months Hosting', '3 Months Domain', '+ ₱1000 for 1 Year Domain and Hosting (Optional)'],
    featured: true,
  },
];

const tiers2 = [
  {
    name: 'Business Class',
    id: 'tier-standard-2',
    href: '#',
    priceMonthly: '₱12,000',
    priceSuffix: 'One Time Payment',
    description: 'Ideal for small businesses needing more capacity.',
    features: ['Landing Page', '3 Pages','5 Email Accounts for your Domain', 'Contact Form', 'Search Engine Optimization', '1 Year Domain', '1 Year Hosting','Blog System', 'CMS Builds applicable (Wordpress, Wix, GoDaddy)'],
    featured: false,
  },
  {
    name: 'Business Class Ultimate',
    id: 'tier-ultimate-2',
    href: '#',
    priceMonthly: '₱18,000',
    priceSuffix: '3500/month',
    description: 'Full-featured plan for power users and growing teams.',
    features: ['Landing Page', '5 Pages','5 Email Accounts for your Domain', 'Contact Form', 'Search Engine Optimization', '1 Year Domain', '1 Year Hosting','Blog System', 'CMS Builds applicable (Wordpress, Wix, GoDaddy)','8am to 5pm - Monday to Friday Support'],
    featured: true,
  },
];

const tiers3 = [
  {
    name: 'Enterprise Basic',
    id: 'tier-basic-3',
    href: '#',
    priceMonthly: '₱55,000',
    priceSuffix: 'One Time Payment',
    description: "Great for Large Businesses that Requires a large amount of details",
    features: ['50 Pages and Above', 'Complex Data Structuring (Database Operated)', '5 Email Accounts', 'Contact Form', 'Search Engine Optimization', '1 Year Domain', '1 Year Hosting', 'Blog System', 'Laravel + VueJS integration'],
    featured: false,
  },
  {
    name: 'Enterprise Ultimate',
    id: 'tier-pro-3',
    href: '#',
    priceMonthly: '₱70,000',
    priceSuffix: '₱12,000/month',
    description: 'For business seeking to outsource their website and its monitoring.',
    features: ['50 Pages and Above', 'Complex Data Structuring (Database Operated)', '5 Email Accounts', 'Contact Form', 'Search Engine Optimization', '1 Year Domain', '1 Year Hosting', 'Blog System', 'Laravel + VueJS integration','Cloudflare Protection','CDN Integration (Faster Page Loads)', '24/7 Support'],
    featured: true,
  },
];

const customSystemTier = ref({
    name: 'Custom System I',
    id: 'tier-lite-4',
    href: '#',
    priceSuffix: '20,000/month',
    basePrice: BASE_PRICE_CUSTOM_SYSTEM_I,
    description: "Click or Tap the Red Text to get quoted.",
    features: [
      { name: 'Login System', isSelected: false },
      { name: 'Dashboard System', isSelected: false },
      { name: 'Complan System', isSelected: false },
      { name: 'Payout System', isSelected: false },
      { name: 'Social Media System', isSelected: false },
      { name: 'Single Sign On Integration', isSelected: false },
      { name: 'Payment Channels', isSelected: false },
      { name: 'All-in One', isSelected: false },
      { name: '24/7 Support', isSelected: false },
    ],
    featured: true,
});

const tiers4 = [
  {
    name: 'E-Commerce (Custom System)',
    id: 'tier-scale-4',
    href: '#',
    priceMonthly: '35,000',
    priceSuffix: 'One Time Payment',
    description: 'Ideal for clients seeking for a dedicated online shop for themselves',
    features: ['Unlimited products', 'Dedicated account manager', 'SLA guarantee', 'Custom integrations', '1 Year Online Shop Subscription'],
    featured: false,
  },
  customSystemTier.value,
];


const allTiers = {
    1: tiers1,
    2: tiers2,
    3: tiers3,
    4: tiers4
};

const activeTierSet = ref(1);

const currentTiers = computed(() => {
  return allTiers[activeTierSet.value];
});

const formattedCustomPrice = computed(() => {
  const selectedFeaturesCount = customSystemTier.value.features.filter(f => f.isSelected).length;
  const totalPrice = BASE_PRICE_CUSTOM_SYSTEM_I + (selectedFeaturesCount * FEATURE_COST);
  return `₱${totalPrice.toLocaleString()}`;
});

const toggleFeature = (feature) => {
  feature.isSelected = !feature.isSelected;
};
</script>
